import { apiClient } from './client';
import type { Patient } from '@/types';

// Extended patient type with calculated fields and doctor relationships
export interface PatientWithDoctors extends Patient {
  age?: number | null;
  bmi?: number | null;
}

// Query params for listing patients
export interface PatientsQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Create patient data
export interface CreatePatientData {
  fullName: string;
  email?: string;
  cpf?: string;
  birthDate?: string;
  weightKg?: number;
  heightCm?: number;
  isInsurance?: boolean;
  insuranceProvider?: string;
  consultationFeeBrl?: number;
  isActive?: boolean;
  doctorIds?: string[];
}

// Update patient data
export interface UpdatePatientData {
  fullName?: string;
  email?: string;
  cpf?: string;
  birthDate?: string;
  weightKg?: number;
  heightCm?: number;
  isInsurance?: boolean;
  insuranceProvider?: string;
  consultationFeeBrl?: number;
  isActive?: boolean;
  doctorIds?: string[];
}

// Paginated response
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const patientsService = {
  // Get all patients with pagination
  async getAll(params?: PatientsQueryParams): Promise<PaginatedResponse<PatientWithDoctors>> {
    const response = await apiClient.get('/patients', { params });
    return response.data;
  },

  // Search patients
  async search(query: string, fields?: string[]): Promise<PatientWithDoctors[]> {
    const response = await apiClient.get('/patients/search', {
      params: { q: query, fields },
    });
    return response.data;
  },

  // Get single patient by ID
  async getById(id: string): Promise<PatientWithDoctors> {
    const response = await apiClient.get(`/patients/${id}`);
    console.log('[patientsService.getById] Response for patient', id, ':', {
      hasUserId: !!response.data.userId,
      userId: response.data.userId
    });
    return response.data;
  },

  // Create new patient
  async create(data: CreatePatientData): Promise<PatientWithDoctors> {
    const response = await apiClient.post('/patients', data);
    return response.data;
  },

  // Update patient
  async update(id: string, data: UpdatePatientData): Promise<PatientWithDoctors> {
    const response = await apiClient.put(`/patients/${id}`, data);
    return response.data;
  },

  // Toggle patient status (Admin only)
  async toggleStatus(id: string): Promise<Patient> {
    const response = await apiClient.patch(`/patients/${id}/status`);
    return response.data;
  },

  // Delete patient (Admin only)
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/patients/${id}`);
  },

  // Get doctors for a patient
  async getDoctors(patientId: string): Promise<string[]> {
    const patient = await this.getById(patientId);
    return patient.doctorIds || [];
  },

  // Add patient to doctor
  async addToDoctor(patientId: string, doctorId: string): Promise<void> {
    await apiClient.post(`/patients/${patientId}/doctors/${doctorId}`);
  },

  // Remove patient from doctor
  async removeFromDoctor(patientId: string, doctorId: string): Promise<void> {
    await apiClient.delete(`/patients/${patientId}/doctors/${doctorId}`);
  },

  // Create user account for patient
  async createUserAccount(patientId: string): Promise<{
    message: string;
    credentials: {
      username: string;
      temporaryPassword: string;
    };
    userId: string;
  }> {
    const response = await apiClient.post(`/patients/${patientId}/create-user`);
    return response.data;
  },
};
